const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');
const normalizePath = (path: string): string => (path.startsWith('/') ? path : `/${path}`);

export const normalizeServerUrl = (value?: string | null): string => {
  const normalized = String(value || '').trim();
  const fallback = 'http://localhost:3001';
  const rawValue = normalized || ((process.env.NODE_ENV || 'development') === 'production' ? '' : fallback);

  if (!rawValue) {
    throw new Error('REACT_APP_CORE_SERVER_URL is required in production.');
  }

  try {
    const parsed = new URL(rawValue);
    const cleanedPath = parsed.pathname.replace(/\/+$/, '').replace(/\/api$/i, '');
    parsed.pathname = cleanedPath || '/';
    return parsed.toString().replace(/\/+$/, '');
  } catch (_error) {
    throw new Error('REACT_APP_CORE_SERVER_URL must be a valid absolute URL.');
  }
};

const parsePositiveInteger = (value: string | undefined, fallback: number): number => {
  const parsed = Number.parseInt(String(value || ''), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const isLocalBrowserHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

/**
 * URL ownership rules:
 * - API requests use REACT_APP_CORE_SERVER_URL / environment.coreServerUrl.
 * - User-facing Elite Core React client links use REACT_APP_ELITE_CORE_CLIENT_URL.
 * - Internal Elite Core users sign in at /login.
 * - Talent / Work Portal users sign in at /talent/login and land at /talent.
 * - Talent account verification is handled by /talent/verify-account.
 */
export const getEliteCoreClientBaseUrl = (): string => {
  const configured = String(process.env.REACT_APP_ELITE_CORE_CLIENT_URL || '').trim();

  if (configured) {
    try {
      return trimTrailingSlash(new URL(configured).toString());
    } catch (_error) {
      throw new Error('REACT_APP_ELITE_CORE_CLIENT_URL must be a valid absolute URL.');
    }
  }

  if (isLocalBrowserHost()) {
    return 'http://localhost:3000';
  }

  throw new Error('REACT_APP_ELITE_CORE_CLIENT_URL is required outside local development.');
};

export const buildEliteCoreClientUrl = (path = '/'): string => (
  `${getEliteCoreClientBaseUrl()}${normalizePath(path)}`
);

export const buildTalentLoginUrl = (): string => buildEliteCoreClientUrl('/talent/login');
export const buildTalentPortalUrl = (): string => buildEliteCoreClientUrl('/talent');
export const buildTalentVerifyAccountUrl = (token?: string): string => {
  const url = new URL(buildEliteCoreClientUrl('/talent/verify-account'));
  if (token) {
    url.searchParams.set('token', token);
  }
  return url.toString();
};

export const environment = {
  coreServerUrl: normalizeServerUrl(process.env.REACT_APP_CORE_SERVER_URL),
  sessionTimeoutMinutes: parsePositiveInteger(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES, 30),
};

export const buildCoreAssetUrl = (path?: string | null): string => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const trimmedPath = path.replace(/^\/+/, '');
  return `${environment.coreServerUrl}/${trimmedPath}`;
};
